import { motion } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";

export const Hero = () => {
  const { language, isRTL } = useLanguage();
  
  const scrollToServices = () => {
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
  };

  const content = {
    ar: {
      badge: "🏭 رواد الصناعات الحديثة في المملكة",
      title: "مجموعة الفنون",
      subtitle: "للصناعات الحديثة",
      tagline: "نبتكر الجمال في كل التفاصيل",
      description: "خبرة متكاملة في صناعة الخشب والحديد والألمنيوم والزجاج والرخام والاستانلس ستيل",
      cta: "تواصل معنا",
      discover: "اكتشف خدماتنا"
    },
    en: {
      badge: "🏭 Leading Modern Industries in the Kingdom",
      title: "ALFUNOON",
      subtitle: "Modern Industries Group",
      tagline: "Crafting Beauty in Every Detail",
      description: "Comprehensive expertise in wood, iron, aluminum, glass, marble, and stainless steel manufacturing",
      cta: "Contact Us",
      discover: "Discover Our Services"
    }
  };

  const t = content[language];

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden" dir={isRTL ? "rtl" : "ltr"}>
      {/* Background Video with Overlay */}
      <div className="absolute inset-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover object-center"
          poster="/lovable-uploads/8e4614d8-6626-4c0e-a776-520dbd598d82.png"
        >
          <source 
            src="https://videos.pexels.com/video-files/3129671/3129671-uhd_2560_1440_30fps.mp4" 
            type="video/mp4" 
          />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
      </div>

      {/* Content */}
      <div className="relative z-20 text-center px-6 md:px-8 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <span className="inline-block bg-primary text-primary-foreground px-5 py-2.5 rounded-full text-sm font-semibold shadow-lg">
            {t.badge}
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-5xl md:text-7xl lg:text-8xl font-kufi font-bold mb-8 text-white leading-tight"
          style={{ textShadow: '2px 4px 8px rgba(0,0,0,0.7)' }}
        >
          {t.title}
          <span className="block text-primary mt-4 drop-shadow-lg">{t.subtitle}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-2xl md:text-3xl font-kufi mb-6 text-white font-medium"
          style={{ textShadow: '1px 2px 4px rgba(0,0,0,0.6)' }}
        >
          {t.tagline}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-lg md:text-xl font-kufi mb-12 text-white/90 max-w-2xl mx-auto leading-relaxed"
          style={{ textShadow: '1px 2px 4px rgba(0,0,0,0.5)' }}
        >
          {t.description}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-5 justify-center items-center"
        >
          <Link to="/contact">
            <button className="bg-primary hover:bg-primary/90 text-primary-foreground px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 shadow-2xl flex items-center gap-3 min-w-[200px] justify-center">
              <Phone className="h-5 w-5" />
              {t.cta}
            </button>
          </Link>
          <button 
            onClick={scrollToServices}
            className="bg-white/15 hover:bg-white/25 text-white px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 backdrop-blur-md border-2 border-white/40 flex items-center gap-3 min-w-[200px] justify-center"
          >
            {t.discover}
            <ArrowDown className="h-5 w-5 animate-bounce" />
          </button>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
      >
        <div className="w-7 h-12 border-2 border-white/60 rounded-full flex justify-center">
          <motion.div
            animate={{ y: [0, 14, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-2 h-4 bg-white/80 rounded-full mt-2"
          />
        </div>
      </motion.div>
    </div>
  );
};
